import type { LevelDef } from '@full-elevator/sim';
import { callButton, coinIcon, el, icon } from './dom.js';
import type { Figure } from './goal.js';
import { goalFigures } from './goal.js';
import { infoButton, tipButton } from './info.js';
import { medalSvg } from './medal.js';

/**
 * The goal card's face (ADR-0037; icon-first since PLAYTEST_LOG 2026-10-29):
 * the shift's plaque and name on its building's lintel, then what wins it as a
 * big number, the star marks in the indicator window, the points rule as three
 * chips, the bonus on one line, and Play. Every sentence it used to say is
 * behind the "i" in its lintel.
 */
export interface GoalCardOptions {
  readonly shift: number;
  /** A hard shift, and whether it is the building's finale. */
  readonly hard: 'hard' | 'finale' | null;
  /** Said once, on the very first shift. */
  readonly welcome: string | null;
  /** The hard shift's sentence, for the "i". */
  readonly hardLine: string | null;
  readonly play: () => void;
  readonly close: () => void;
}

/** A figure, its caption under it, and what it means at a tap (PLAYTEST_LOG 2026-10-30). */
function figure(f: Figure, big: boolean, tone?: 'good' | 'bad'): HTMLElement {
  const face = el('span', { class: big ? 'goal-figure' : 'chip' }, [
    ...(f.icon ? [el('span', { class: 'goal-figure-icon' }, [icon(f.icon)])] : []),
    el('span', { class: 'goal-figure-value', text: f.value }),
    ...(f.of ? [el('small', { text: f.of })] : []),
  ]);
  if (tone) face.dataset.tone = tone;
  const parts = [face, ...(f.label ? [el('span', { class: 'goal-label', text: f.label })] : [])];
  const title = `${f.value}${f.of ? ` ${f.of}` : ''}${f.label ? ` ${f.label}` : ''}`;
  return f.tip
    ? tipButton(big ? 'goal-sign goal-sign-big' : 'goal-sign', title, [f.tip], parts)
    : el('span', { class: 'goal-sign' }, parts);
}

/** What the star marks mean, in the unit they are in. */
function starsTip(unit: 'clock' | 'trip' | null, three: string, two: string): string[] {
  if (unit === 'clock') return [`Win in ${three} or less for three stars, ${two} for two.`];
  if (unit === 'trip') return [`Win in ${three} trips or fewer for three stars, ${two} for two.`];
  return [`End with ${three} points or more for three stars, ${two} for two.`];
}

function starCell(count: number, value: string, unit: 'clock' | 'trip' | null): HTMLElement {
  const mark = unit
    ? [icon(unit), value]
    : [value, el('small', { class: 'window-unit', text: 'pts' })];
  return el('div', {}, [
    el(
      'span',
      { class: 'window-stars' },
      Array.from({ length: 3 }, (_, i) => {
        const star = icon('star');
        star.dataset.on = String(i < count);
        return star;
      }),
    ),
    el('span', { class: 'window-value' }, mark),
  ]);
}

export function goalCard(level: LevelDef, options: GoalCardOptions): HTMLElement[] {
  const f = goalFigures(level);
  const more = [
    ...(options.welcome ? [options.welcome] : []),
    ...(options.hardLine ? [options.hardLine] : []),
    ...f.more,
  ];
  const go = el('button', { class: 'primary goal-play', type: 'button' }, [icon('play'), 'Play']);
  go.addEventListener('click', options.play);
  const top = el(
    'div',
    { class: 'goal-top', 'data-building': level.buildingId ?? 'office-tower' },
    [
      el('span', { class: 'plaque', text: String(options.shift).padStart(2, '0') }),
      el('h3', { text: level.name }),
      infoButton(level.name, more),
      // Not now: back to the results or the building it was opened from, so a
      // part can be fitted first (the owner, PLAYTEST_LOG 2026-10-28).
      callButton('close', 'Not now', options.close),
    ],
  );
  const lines: HTMLElement[] = [];
  if (options.hard) {
    lines.push(
      el('div', { class: 'goal-hard', 'data-finale': String(options.hard === 'finale') }, [
        el('strong', { text: options.hard === 'finale' ? 'Finale' : 'Hard shift' }),
        el('span', { class: 'goal-hard-pay' }, [coinIcon(), '×1.5']),
      ]),
    );
  }
  if (options.welcome) lines.push(el('div', { class: 'goal-welcome', text: options.welcome }));
  lines.push(
    el(
      'div',
      { class: 'goal-hero' },
      f.hero.map((h) => figure(h, true)),
    ),
    tipButton('goal-window', 'Stars', starsTip(f.stars.icon, f.stars.three, f.stars.two), [
      starCell(3, f.stars.three, f.stars.icon),
      starCell(2, f.stars.two, f.stars.icon),
    ]),
  );
  if (f.scoring.length > 0) {
    lines.push(
      el(
        'div',
        { class: 'goal-chips' },
        f.scoring.map((s) => figure(s, false, s.good ? 'good' : 'bad')),
      ),
    );
  }
  if (f.bonus) {
    lines.push(
      tipButton('goal-bonus', 'Bonus', f.bonus.tip, [
        medalSvg(false),
        el('span', { class: 'goal-label', text: 'Bonus' }),
        el('span', { class: 'goal-bonus-text' }, [
          ...(f.bonus.icon ? [icon(f.bonus.icon)] : []),
          f.bonus.text,
        ]),
        el('span', { class: 'chip' }, [coinIcon(), `+${f.bonus.coins}`]),
      ]),
    );
  }
  lines.push(el('p', { class: 'goal-hint', text: 'Tap a sign to see what it means.' }), go);
  return [top, el('div', { class: 'goal-main' }, lines)];
}
